<template>
  <OnexAlert
    v-if="message !== ''"
    :type="Notifications.error"
    dismissible="true"
    @click:close="message = ''"
  >
    <span>{{ $t('L_generic_error_message') }}</span>
  </OnexAlert>
  <v-container>
    <v-row>
      <v-col cols="12">
        <v-card id="policy_ack_card" class="space_to_go_under_bottom_button policy_ack_card_css">
          <div class="text-left">
            <div class="text-weight-regular text-h1 pl-4 pr-4 pt-4 pb-4">
              {{ $t('L_policy_acknowledgement') }}
            </div>
          </div>
          <v-divider py-4 class="line_separator" />
          <v-row class="text-weight-bold pt-4 pl-4 pr-4 pb-4">
            <v-col cols="12">
              <h3 class="text-h4 font-weight-regular">
                {{ $t(verbiage.byclickingbelow, { policynum: policynumber }) }}
              </h3>
            </v-col>
            <v-col cols="12" class="pb-0">
              <OnexButton
                @click="showPolicy"
                :text="$t('L_view_full_policy')"
                variant="text"
                tabindex="0"
              ></OnexButton>
            </v-col>
          </v-row>
          <v-col class="pt-0 pl-0 pr-0 pb-4">
            <v-card-text class="font-weight-regular" v-if="isCanadian">
              <span class="font-size--16">
                {{ $t('L_by_clicking_i_agree_i_acknowledge_CAN') }}
              </span>
            </v-card-text>
            <v-card-text
              class="font-weight-regular"
              v-if="verbiage.acknowledge != '' && !isCanadian"
            >
              <span class="font-size--16">
                {{ $t(verbiage.acknowledge) }}
              </span>
            </v-card-text>
            <v-card-text
              class="font-weight-regular"
              v-if="verbiage.understand != '' && !isCanadian"
            >
              <span class="font-size--16">
                {{ $t(verbiage.understand) }}
              </span>
            </v-card-text>
            <v-card-text v-if="!isCanadian">
              <div
                class="pl-4 pr-4 pb-0 iosIframeScroller"
                :class="
                  $vuetify.display.xs ? 'iframeContentHeightMobile' : 'iframeContentHeightDesktop'
                "
              >
                <iframe
                  id="edelivery_disclosure_iframe"
                  :class="$vuetify.display.xs ? 'iframe_mobile' : 'iframe_desktop'"
                  v-if="htmlSrc"
                  :src="htmlSrc"
                  title="e-delivery disclosure html"
                />
              </div>
              <div class="gradientHover pl-4 mt-2 pr-4">&nbsp;</div>
            </v-card-text>
            <v-row>
              <v-col cols="12" class="text-center my-4">
                <OnexButton
                  color="primary"
                  @click="iAgree"
                  :class="
                    $vuetify.display.xs
                      ? 'fixed_bottom_button_mobile'
                      : 'fixed_bottom_button_desktop'
                  "
                >
                  {{ $t('L_i_accept') }}
                </OnexButton>
              </v-col>
            </v-row>
          </v-col>
        </v-card>
      </v-col>
    </v-row>
    <EmbeddedViewer
      title="Life Policy"
      :loading="progressLoading"
      content-type="application/pdf"
      :binary-data="documentContent"
    />
  </v-container>
</template>

<script lang="ts">
import { mapState, mapActions } from 'pinia';
import { COUNTRIES } from '@/shared/models/constants.models';
import EmbeddedViewer from '@/components/EmbeddedViewer.vue';
import DynatraceClient from '@/plugins/dynatracePlugin';
import { openInNewWindow } from '@/mixins/openNewWindow';
import { useGlobalStore } from '@/stores/global/global.store';
import { usePolicyStore } from '@/modules/policy/store/policy.store';
import { useCustomerStore } from '@/stores/customer/customer.store';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import type { IDocumentMetadata } from '@/shared/api/models.content';
import type {
  IPolicyStorePolicy,
  IPolicyStorePolicyPhases,
} from '@/modules/policy/store/policyStore.model';
import { OnexButton, Notifications, OnexAlert } from '@primerica/onex-ui-lib';
import type { IGlobalStoreTask } from '@/stores/global/globalStore.model.ts';
import { RouteNames } from '@/router/routeNames';

import { defineComponent, type ComponentOptions } from 'vue';

const componentOptions: ComponentOptions = {
  name: 'PolicyAcknowledgementAction',
  components: {
    EmbeddedViewer,
    OnexButton,
    OnexAlert,
  },
  mixins: [openInNewWindow],
  data: () => ({
    policynumber: '',
    text: undefined,
    progressLoading: false,
    documentContent: undefined,
    Notifications,
    message: '',
    type: 'error',
  }),
  computed: {
    ...mapState(useCustomerStore, ['binaryInvestmentStatement', 'customerProfile']),
    ...mapState(usePolicyStore, ['currentPolicy', 'policies', 'selectedPolicyIndex']),
    ...mapState(useGlobalStore, [
      'getLocale',
      'getFullPolicyDocument',
      'containsFeature',
      'tasks',
      'base64String',
    ]),
    getType() {
      switch (this.type) {
        case 'error':
          return Notifications.error;
        case 'success':
          return Notifications.success;
        case 'warning':
          return Notifications.warning;
        default:
          return Notifications.info;
      }
    },
    htmlSrc() {
      if (this.getLocale === 'en_CA') {
        return window.appConfig.ecomm.en_CA.ecommunication_consent;
      } else if (this.getLocale === 'fr_CA') {
        return window.appConfig.ecomm.fr_CA.ecommunication_consent;
      }
      return window.appConfig.ecomm.en_US.ecommunication_consent;
    },
    isCanadian() {
      return this.customerProfile?.country === COUNTRIES.CANADA;
    },
    verbiage(): {
      byclickingbelow: string;
      acknowledge: string;
      understand: string;
    } {
      let state = '';
      const BI_STATE = [
        'AK',
        'AL',
        'CA',
        'CO',
        'CT',
        'DE',
        'HI',
        'IA',
        'IL',
        'IN',
        'KS',
        'KY',
        'LA',
        'MA',
        'MD',
        'ME',
        'MN',
        'MO',
        'MS',
        'MT',
        'NC',
        'ND',
        'NE',
        'NH',
        'NJ',
        'NM',
        'NV',
        'OH',
        'OK',
        'OR',
        'PA',
        'RI',
        'SC',
        'SD',
        'TX',
        'UT',
        'VT',
        'WA',
        'WI',
        'WV',
      ];
      const BI_STATE_NY = ['NY'];
      const NON_BI_STATE = ['AR', 'AZ', 'DC', 'FL', 'GA', 'ID', 'MI', 'TN', 'VA', 'WY'];
      if (this.currentPolicy?.phases != null) {
        const phases = this.currentPolicy?.phases;
        phases.forEach((phase: IPolicyStorePolicyPhases | null) => {
          // Look at the phase Issue State
          if (phase?.phaseNo === '01') {
            state = phase.issueState;
          }
        });
      }
      if (state !== '') {
        if (BI_STATE_NY.find((a) => a === state) !== undefined) {
          return {
            byclickingbelow: 'L_policy_ack_top_text_NY',
            acknowledge: 'L_i_also_acknowledge_NY',
            understand: 'L_i_also_understand_NY',
          };
        } else if (NON_BI_STATE.find((a) => a === state) !== undefined) {
          return {
            byclickingbelow: 'L_policy_ack_top_text_Non_BI',
            acknowledge: 'L_i_also_acknowledge_Non_BI',
            understand: 'L_i_also_understand_Non_BI',
          };
        } else if (BI_STATE.find((a) => a === state) !== undefined) {
          return {
            byclickingbelow: 'L_policy_ack_top_text',
            acknowledge: 'L_i_also_acknowledge',
            understand: 'L_i_also_understand',
          };
        }
        return {
          byclickingbelow: 'L_policy_ack_top_text',
          acknowledge: 'L_i_also_acknowledge',
          understand: 'L_i_also_understand',
        };
      }
      return {
        byclickingbelow: 'L_policy_ack_top_text',
        acknowledge: 'L_i_also_acknowledge',
        understand: 'L_i_also_understand',
      };
    },
  },
  async mounted() {
    this.getPolicyAckNotShown();
    // ! consider policyStore.hasTermLife
    if (this.policies.length > 0 && this.policies[0].contractId === '0') {
      await this.getPolicyOverview();
    }
    // This fixes the policy with POLICY_ACK being 2nd or later policy in multiple policy
    // mypriteam+be@gmail.com for testing in UAT
    const tempIndex = this.policies.findIndex(
      (policy: IPolicyStorePolicy) => policy.contractId === this.policynumber
    );
    this.selectedPolicyIndex = tempIndex;

    // const iframe = document.getElementById(
    //   'edelivery_disclosure_iframe'
    // ) as HTMLIFrameElement | null;
    // if (iframe?.style && iframe.contentWindow?.document.body) {
    //   iframe.style.height = `${iframe.contentWindow.document.body.scrollHeight}px`;
    // }
  },
  methods: {
    ...mapActions(useGlobalStore, [
      'addError',
      'getOnePolicyDocument',
      'getAllPolicyDocuments',
      'removeAlerts',
      'ackPolicy',
    ]),
    ...mapActions(usePolicyStore, ['getPolicyOverview']),
    getPolicyAckNotShown() {
      const idx = this.tasks.findIndex(
        (a: IGlobalStoreTask) => a.name === 'POLICY_ACK' && a.shownFlag === false
      );
      if (idx !== -1) {
        this.policynumber = this.tasks[idx]?.policyNumbers?.[0] || '';
      } else {
        this.policynumber = '';
      }
    },
    async showPolicy() {
      // MYP-6960 Policy Acknowledgement - Create custom Dynatrace tags for research and tracking purposes
      try {
        const i = DynatraceClient.enterAction(
          `PC2_PolicyAcknowledgement_ViewFullPolicy_${this.policynumber}`
        );
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      /* Find the Policy Document */
      const policyDoc = this.getFullPolicyDocument(this.policynumber);

      // commit 27a278c4b2a8e15c7c9b438169993863d78a13e0
      const url = await this.getAllDocumentsUrl(policyDoc?.metadata);
      // .catch((e: unknown) => {
      //   this.addError({ error: e, context: 'policy_ack' });
      // });
      const isWeb = CapacitorFacade.isWeb();
      await this.openInNewWindow({ url, target: '_blank', inappbrowser: !isWeb });
    },
    getPolicyDocFromPolicies(policyNumToGet: string) {
      let resultDoc = null;
      try {
        const matchingPolicyList = this.policies.filter((policy: IPolicyStorePolicy) =>
          policy.contractId.includes(policyNumToGet)
        );
        for (const doc of matchingPolicyList[0]._documents) {
          if (
            doc.policyNum === policyNumToGet &&
            (doc.documentType === 'A' || doc.documentType === 'S')
          ) {
            resultDoc = doc;
          }
        }
      } catch (_e) {
        // Empty block
      }
      return resultDoc;
    },
    // ***
    getSingleDocumentUrl(metadata: IDocumentMetadata | undefined) {
      if (!metadata) {
        return Promise.reject(new Error('no document metadata'));
      }
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return this.getOnePolicyDocument(metadata)
          .then(() =>
            URL.createObjectURL(
              new Blob(this.base64String as BlobPart[], { type: 'application/pdf' })
            )
          )
          .catch((e: unknown) => {
            try {
              // eslint-disable-next-line no-console
              console.error(
                `[PolicyAcknowledgementAction ~ getSingleDocumentUrl] ${
                  typeof e === 'object' && e && 'code' in e ? e.code : ''
                }: ${typeof e === 'object' && e && 'message' in e ? e.message : JSON.stringify(e)}`
              );
              DynatraceClient.reportCustomError(
                'PC2_MOBILE_ERR',
                `[PolicyAcknowledgementAction ~ getSingleDocumentUrl] ${
                  typeof e === 'object' && e && 'code' in e ? e.code : ''
                }: ${typeof e === 'object' && e && 'message' in e ? e.message : JSON.stringify(e)}`
              );
            } catch (_error) {
              /* empty */
            }
          });
      } else {
        return this.getOnePolicyDocument(metadata)
          .then(() => this.base64String)
          .catch((e: unknown) => {
            try {
              // eslint-disable-next-line no-console
              console.error(
                `[PolicyAcknowledgementAction ~ getSingleDocumentUrl] ${
                  typeof e === 'object' && e && 'code' in e ? e.code : ''
                }: ${typeof e === 'object' && e && 'message' in e ? e.message : JSON.stringify(e)}`
              );
              DynatraceClient.reportCustomError(
                'PC2_MOBILE_ERR',
                `[PolicyAcknowledgementAction ~ getSingleDocumentUrl] ${
                  typeof e === 'object' && e && 'code' in e ? e.code : ''
                }: ${typeof e === 'object' && e && 'message' in e ? e.message : JSON.stringify(e)}`
              );
            } catch (_error) {
              /* empty */
            }
          });
      }
    },
    // ***
    async getAllDocumentsUrl(metadata: IDocumentMetadata | undefined) {
      if (!metadata) {
        return Promise.reject(new Error('no document metadata'));
      }
      const isWeb = CapacitorFacade.isWeb();
      if (isWeb) {
        return await this.getAllPolicyDocuments(metadata)
          .then(() =>
            URL.createObjectURL(
              new Blob([this.base64String as BlobPart], { type: 'application/pdf' })
            )
          )
          .catch((e: unknown) => {
            try {
              // eslint-disable-next-line no-console
              console.error(
                `[PolicyAcknowledgementAction ~ getAllDocumentsUrl] ${
                  typeof e === 'object' && e && 'code' in e ? e.code : ''
                }: ${typeof e === 'object' && e && 'message' in e ? e.message : JSON.stringify(e)}`
              );
              DynatraceClient.reportCustomError(
                'PC2_MOBILE_ERR',
                `[PolicyAcknowledgementAction ~ getAllDocumentsUrl] ${
                  typeof e === 'object' && e && 'code' in e ? e.code : ''
                }: ${typeof e === 'object' && e && 'message' in e ? e.message : JSON.stringify(e)}`
              );
            } catch (_error) {
              /* empty */
            }
          });
      } else {
        return await this.getAllPolicyDocuments(metadata)
          .then(() => this.base64String)
          .catch((e: unknown) => {
            try {
              // eslint-disable-next-line no-console
              console.error(
                `[PolicyAcknowledgementAction ~ getAllDocumentsUrl] ${
                  typeof e === 'object' && e && 'code' in e ? e.code : ''
                }: ${typeof e === 'object' && e && 'message' in e ? e.message : JSON.stringify(e)}`
              );
              DynatraceClient.reportCustomError(
                'PC2_MOBILE_ERR',
                `[PolicyAcknowledgementAction ~ getAllDocumentsUrl] ${
                  typeof e === 'object' && e && 'code' in e ? e.code : ''
                }: ${typeof e === 'object' && e && 'message' in e ? e.message : JSON.stringify(e)}`
              );
            } catch (_error) {
              /* empty */
            }
          });
      }
    },
    async iAgree() {
      // MYP-6960 Policy Acknowledgement - Create custom Dynatrace tags for research and tracking purposes
      try {
        const i = DynatraceClient.enterAction(
          `PC2_PolicyAcknowledgement_IAgree_${this.policynumber}`
        );
        DynatraceClient.leaveAction(i);
      } catch (_error) {
        /* empty */
      }

      try {
        this.message = '';
        // this.removeAlerts('policy_ack');
        const data = {
          policyNum: this.policynumber,
          locale: this.getLocale,
        };
        let ackPolicyResp;
        try {
          ackPolicyResp = await this.ackPolicy(data);
        } catch (error) {
          this.message = this.$t('L_generic_error_message');
          this.type = 'error';
          try {
            DynatraceClient.reportCustomError(
              'PC2_MOBILE_ERR',
              `[PolicyAcknowledgementAction ~ iAgree] ${
                typeof error === 'object' && error && 'code' in error ? error.code : ''
              }: ${
                typeof error === 'object' && error && 'message' in error
                  ? error.message
                  : JSON.stringify(error)
              }`
            );
          } catch (_e) {
            /* empty */
          }
        }
        if (ackPolicyResp === true) {
          this.setShownFlag(this.policynumber);
          const deeplinkroute = window.localStorage.getItem('deeplinkroute'); // ? this is a path, NOT name of a route
          if (deeplinkroute) window.localStorage.removeItem('deeplinkroute'); // remove the deeplink route
          const nextRoute = deeplinkroute ?? RouteNames.DASHBOARD;
          const nextRouteIsNamedRoute = Object.values<string>(RouteNames).includes(nextRoute);
          const _nextRouteOrPath = nextRouteIsNamedRoute
            ? { name: nextRoute }
            : { path: nextRoute };
          await this.$router.push(_nextRouteOrPath);
        } else {
          this.message = this.$t('L_generic_error_message');
          this.type = 'error';
        }
      } catch (e: unknown) {
        try {
          // eslint-disable-next-line no-console
          console.error(`[PolicyAcknowledgementAction ~ iAgree] ${JSON.stringify(e)}`);
          DynatraceClient.reportCustomError(
            'PC2_MOBILE_ERR',
            `[PolicyAcknowledgementAction ~ iAgree] ${JSON.stringify(e)}`
          );
        } catch (_error) {
          /* empty */
        }
      }
    },
    setShownFlag(policyNum: string) {
      // To find a specific object in an array of objects
      const idx = this.tasks.findIndex(
        (a: IGlobalStoreTask) => a.name === 'POLICY_ACK' && a.policyNumbers?.[0] === policyNum
      );
      if (idx !== -1) {
        this.tasks[idx].shownFlag = true;
      }
    },
  },
};

export default defineComponent(componentOptions);
</script>

<style lang="scss" scoped>
.line_separator {
  border: 1px solid #bcbaba;
}
.policy_ack_card_css {
  max-width: 960px;
  margin: auto;
}
.bg-light-grey {
  background-color: #f5f5f5;
}
.paddingSpaceAroundEntireBox {
  padding: 0 40px 0px 40px;
}
.iframe_mobile {
  width: 100%;
  border: 2px solid black;
  height: calc(100vh - 370px) !important;
}
.iframe_desktop {
  width: 100%;
  border: 2px solid black;
  height: calc(100vh - 385px);
}
.iframeContentHeightMobile {
  height: calc(100vh - 370px);
}
.iframeContentHeightDesktop {
  height: calc(100vh - 385px);
}
.fixed_bottom_button_mobile {
  bottom: 10px;
  width: 280px !important;
  margin: auto !important;
}
.fixed_bottom_button_desktop {
  bottom: 10px;
  width: 280px !important;
  margin: auto !important;
}
.space_to_go_under_bottom_button {
  padding-bottom: 150px;
}
.remove_underline_link {
  text-decoration: none !important;
  color: #02579e;
}
.button-outline-color {
  outline: none !important;
}
.iosIframeScroller {
  -webkit-overflow-scrolling: touch !important;
  overflow-y: scroll !important;
}
</style>
